/* ============================================================
   LKWood Sneak Peek Keyhole - JS (v1.1 · Perf Optimized)
   ------------------------------------------------------------
   Perf principles:
   • IntersectionObserver → only run heavy work when visible
   • Page Visibility API → pause when tab hidden
   • Throttle mouse tracking on touch (30fps vs 60fps)
   • Debounce localStorage (avoid I/O on every load)
   • Fetch timeout 8s → never block checkout
   • Passive event listeners → no scroll jank
   • Zero global state pollution, scoped per section
   ============================================================ */

(function () {
  'use strict';

  // ---------- Feature detects (compute once) ----------
  var HAS_IO   = typeof IntersectionObserver !== 'undefined';
  var IS_TOUCH = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
  var REDUCED  = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var STORAGE_KEY = 'lkw-sneak-peek-count';
  var STORAGE_TS_KEY = 'lkw-sneak-peek-count-ts';
  var STORAGE_MIN_INTERVAL = 30000; // debounce localStorage writes to 30s

  // ---------- Init all sections on page ----------
  function init() {
    var nodes = document.querySelectorAll('[data-lkw-sneak-peek]');
    for (var i = 0; i < nodes.length; i++) setupSection(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ============================================================
  // Setup a single section — GATED by IntersectionObserver
  // ============================================================
  function setupSection(root) {
    if (root.dataset.lkwInit === '1') return;
    root.dataset.lkwInit = '1';

    // Email + spotlight are lightweight & user-initiated → init immediately
    setupEmail(root);
    setupKeyhole(root);

    // Heavy (countdown timer, social counter) → defer until visible
    var heavy = function () {
      setupCountdown(root);
      setupSocialCounter(root);
    };

    if (HAS_IO) {
      var io = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            heavy();
            io.disconnect();
            break;
          }
        }
      }, { rootMargin: '200px 0px' }); // pre-warm 200px before entering viewport
      io.observe(root);
    } else {
      // Old browsers → just run
      heavy();
    }
  }

  // ============================================================
  // COUNTDOWN — pause when off-screen / tab hidden
  // ============================================================
  function setupCountdown(root) {
    var cd = root.querySelector('[data-lkw-countdown]');
    if (!cd) return;

    var launchISO   = root.dataset.launch;
    var launchTime  = Date.parse(launchISO);
    if (isNaN(launchTime)) {
      // Silent fail — never block user experience with console spam in prod
      return;
    }

    var $days = cd.querySelector('[data-lkw-days]');
    var $hrs  = cd.querySelector('[data-lkw-hours]');
    var $min  = cd.querySelector('[data-lkw-mins]');
    var $sec  = cd.querySelector('[data-lkw-secs]');
    var $boxes = cd.querySelector('.lkw-sp__countdown-boxes');
    var $label = cd.querySelector('.lkw-sp__countdown-label');
    var $launched = cd.querySelector('[data-lkw-launched]');

    var timer = null;
    var isVisible = true;
    var isTabActive = !document.hidden;

    function pad(n) { return n < 10 ? '0' + n : '' + n; }

    function tick() {
      var diff = launchTime - Date.now();

      if (diff <= 0) {
        if ($boxes) $boxes.style.display = 'none';
        if ($label) $label.style.display = 'none';
        if ($launched) $launched.hidden = false;
        root.dispatchEvent(new CustomEvent('lkw:launched', { bubbles: true }));
        stop();
        return;
      }

      // Bit-shift math is trivially faster than Math.floor for positive ints
      var days = (diff / 86400000) | 0;
      var hrs  = ((diff % 86400000) / 3600000) | 0;
      var min  = ((diff % 3600000) / 60000) | 0;
      var sec  = ((diff % 60000) / 1000) | 0;

      if ($days && $days.textContent !== pad(days)) $days.textContent = pad(days);
      if ($hrs  && $hrs.textContent  !== pad(hrs))  $hrs.textContent  = pad(hrs);
      if ($min  && $min.textContent  !== pad(min))  $min.textContent  = pad(min);
      if ($sec  && $sec.textContent  !== pad(sec))  $sec.textContent  = pad(sec);
    }

    function start() {
      if (timer) return;
      tick();
      timer = setInterval(tick, 1000);
    }
    function stop() {
      if (!timer) return;
      clearInterval(timer);
      timer = null;
    }
    function refresh() {
      if (isVisible && isTabActive) start();
      else stop();
    }

    // Pause when off-screen (saves CPU when user scrolls away)
    if (HAS_IO) {
      var io = new IntersectionObserver(function (entries) {
        isVisible = entries[0].isIntersecting;
        refresh();
      });
      io.observe(cd);
    }

    // Pause when tab is hidden (Chrome will throttle anyway, but this is cleaner)
    document.addEventListener('visibilitychange', function () {
      isTabActive = !document.hidden;
      if (isTabActive) tick(); // catch up immediately when returning
      refresh();
    });

    start();
  }

  // ============================================================
  // KEYHOLE — mouse tracking + click to open
  // Optimized: throttle rAF on touch (~30fps instead of 60fps)
  // ============================================================
  function setupKeyhole(root) {
    var door = root.querySelector('[data-lkw-door]');
    if (!door) return;

    var rect = null;
    var raf = null;
    var lastTouchTick = 0;
    var TOUCH_THROTTLE = 33; // ~30fps on touch — saves battery, imperceptible
    var isPeeking = false;

    function measure() { rect = door.getBoundingClientRect(); }

    function setSpot(x, y) {
      var w = rect ? rect.width : door.offsetWidth;
      var spotSize = Math.max(120, Math.min(260, w * 0.42));
      // Batch style writes into single reflow
      var s = door.style;
      s.setProperty('--lkw-x', x + 'px');
      s.setProperty('--lkw-y', y + 'px');
      s.setProperty('--lkw-spot', spotSize + 'px');
    }

    function extractPoint(e) {
      if (e.touches && e.touches[0]) {
        return { x: e.touches[0].clientX, y: e.touches[0].clientY };
      }
      return { x: e.clientX, y: e.clientY };
    }

    function onMove(e) {
      if (!rect) measure();

      // Throttle touch to 30fps (mouse stays at 60fps)
      if (e.touches) {
        var now = performance.now();
        if (now - lastTouchTick < TOUCH_THROTTLE) return;
        lastTouchTick = now;
      }

      var p = extractPoint(e);
      var cx = p.x - rect.left;
      var cy = p.y - rect.top;

      if (raf) return; // coalesce rapid events into single rAF
      raf = requestAnimationFrame(function () {
        raf = null;
        setSpot(cx, cy);
      });

      if (!isPeeking) {
        isPeeking = true;
        door.classList.add('is-peeking');
      }
    }

    function onLeave() {
      isPeeking = false;
      door.classList.remove('is-peeking');
    }

    // Mouse (passive where possible)
    door.addEventListener('mouseenter', measure);
    door.addEventListener('mousemove', onMove);
    door.addEventListener('mouseleave', onLeave);

    // Touch (all passive → never blocks scroll)
    var touchTimer = null;
    door.addEventListener('touchstart', function (e) {
      measure();
      onMove(e);
      if (touchTimer) { clearTimeout(touchTimer); touchTimer = null; }
    }, { passive: true });
    door.addEventListener('touchmove', onMove, { passive: true });
    door.addEventListener('touchend', function () {
      touchTimer = setTimeout(onLeave, 2500);
    }, { passive: true });

    // Keyboard
    door.addEventListener('focus', function () {
      measure();
      setSpot(rect.width / 2, rect.height / 2);
      door.classList.add('is-peeking');
      isPeeking = true;
    });
    door.addEventListener('blur', onLeave);

    function toggleOpen() {
      if (REDUCED) {
        door.classList.add('is-peeking');
        setSpot((rect ? rect.width : door.offsetWidth) / 2, (rect ? rect.height : door.offsetHeight) / 2);
        door.style.setProperty('--lkw-spot', '500px');
        return;
      }
      door.classList.toggle('is-open');
    }
    door.addEventListener('click', toggleOpen);
    door.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggleOpen();
      }
    });

    // Invalidate rect on resize/scroll (passive, no jank)
    window.addEventListener('resize', function () { rect = null; }, { passive: true });
    window.addEventListener('scroll', function () { rect = null; }, { passive: true });
  }

  // ============================================================
  // EMAIL SUBMIT — 8s timeout so never blocks user
  // ============================================================
  function setupEmail(root) {
    var form = root.querySelector('[data-lkw-form]');
    if (!form) return;

    var input = form.querySelector('input[type="email"]');
    var btn = form.querySelector('[data-lkw-submit]');
    var btnText = form.querySelector('.lkw-sp__btn-text');
    var btnLoading = form.querySelector('.lkw-sp__btn-loading');
    var msg = form.querySelector('[data-lkw-msg]');

    var provider   = root.dataset.emailProvider || 'shopify';
    var klaviyoList = root.dataset.klaviyoList || '';
    var klaviyoKey  = root.dataset.klaviyoKey || '';

    var successMessage = form.dataset.successMsg
      || '✨ You\'re on the guest list. Check your email!';

    function setState(state, message) {
      if (!msg) return;
      msg.textContent = message || '';
      msg.dataset.type = state || '';
    }

    function setLoading(loading) {
      if (!btn) return;
      btn.disabled = loading;
      if (btnText) btnText.hidden = loading;
      if (btnLoading) btnLoading.hidden = !loading;
    }

    function validEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var email = (input.value || '').trim();

      if (!validEmail(email)) {
        setState('error', 'Please enter a valid email address.');
        input.focus();
        return;
      }

      setLoading(true);
      setState('info', 'Sending your invitation…');

      var submitPromise = (provider === 'klaviyo')
        ? submitKlaviyo(email, klaviyoList, klaviyoKey)
        : submitShopify(email);

      // Hard 8s timeout — user never gets stuck if provider is slow/down
      var timeoutPromise = new Promise(function (_, reject) {
        setTimeout(function () { reject(new Error('Request timed out. Please try again.')); }, 8000);
      });

      Promise.race([submitPromise, timeoutPromise])
        .then(function () {
          setState('success', successMessage);
          input.value = '';
          form.dataset.submitted = '1';
          bumpSocialCount(root);
          root.dispatchEvent(new CustomEvent('lkw:sneak-peek-signup', {
            bubbles: true,
            detail: { email: email, provider: provider }
          }));
        })
        .catch(function (err) {
          setState('error', (err && err.message) || 'Something went wrong. Please try again.');
        })
        .then(function () {
          setLoading(false);
        });
    });

    var successAttr = root.getAttribute('data-email-success');
    if (successAttr) form.dataset.successMsg = successAttr;
  }

  // ---------- Klaviyo submit (with AbortController) ----------
  function submitKlaviyo(email, listId, publicKey) {
    if (!listId || !publicKey) {
      return Promise.reject(new Error('Klaviyo not configured. Please contact the store.'));
    }
    var endpoint = 'https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(publicKey);
    var body = {
      data: {
        type: 'subscription',
        attributes: {
          profile: { data: { type: 'profile', attributes: { email: email } } },
          custom_source: 'LKWood Sneak Peek'
        },
        relationships: { list: { data: { type: 'list', id: listId } } }
      }
    };
    return fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'revision': '2024-10-15' },
      body: JSON.stringify(body),
      keepalive: true // survive navigation → improves reliability
    }).then(function (res) {
      if (!res.ok && res.status !== 202) {
        return res.json().catch(function () { return null; }).then(function (j) {
          var errText = (j && j.errors && j.errors[0] && j.errors[0].detail) || ('Klaviyo error ' + res.status);
          throw new Error(errText);
        });
      }
      return true;
    });
  }

  // ---------- Shopify Customer submit ----------
  function submitShopify(email) {
    var formData = new FormData();
    formData.append('form_type', 'customer');
    formData.append('utf8', '✓');
    formData.append('contact[email]', email);
    formData.append('contact[tags]', 'sneak-peek,lkwood-early-access');

    return fetch('/contact#contact_form', {
      method: 'POST',
      body: formData,
      headers: { 'Accept': 'text/html' },
      keepalive: true
    }).then(function (res) {
      // Shopify 200 = OK, 302 = redirect after submit (both success)
      if (!res.ok && res.status !== 302 && res.status !== 0) {
        throw new Error('Subscription failed. Please try again.');
      }
      return true;
    });
  }

  // ============================================================
  // SOCIAL PROOF COUNTER — debounced localStorage writes
  // ============================================================
  function setupSocialCounter(root) {
    var el = root.querySelector('[data-lkw-social-text]');
    if (!el) return;

    var base = parseInt(root.dataset.socialBase, 10) || 100;
    var stored, lastTs;
    try {
      stored = parseInt(localStorage.getItem(STORAGE_KEY), 10);
      lastTs = parseInt(localStorage.getItem(STORAGE_TS_KEY), 10) || 0;
    } catch (_) {
      // Private browsing / storage disabled → fallback silently
      stored = NaN; lastTs = 0;
    }
    if (isNaN(stored) || stored < base) stored = base;

    // Only bump + write if 30s+ passed (debounce to avoid I/O on every SPA nav)
    var now = Date.now();
    if (now - lastTs > STORAGE_MIN_INTERVAL) {
      stored += (Math.random() * 3) | 0; // 0-2 increment
      try {
        localStorage.setItem(STORAGE_KEY, stored);
        localStorage.setItem(STORAGE_TS_KEY, now);
      } catch (_) { /* ignore quota errors */ }
    }

    updateSocialText(el, stored);
  }

  function updateSocialText(el, count) {
    var template = el.textContent;
    // Normalize: convert any existing number or {count} to %C%
    if (template.indexOf('%C%') === -1 && template.indexOf('{count}') === -1) {
      template = template.replace(/\d[\d,]*/, '%C%');
    }
    template = template.replace('{count}', '%C%');
    el.textContent = template.replace('%C%', count.toLocaleString());
  }

  function bumpSocialCount(root) {
    var el = root.querySelector('[data-lkw-social-text]');
    if (!el) return;
    var cur;
    try {
      cur = parseInt(localStorage.getItem(STORAGE_KEY), 10) || 100;
      cur += 1;
      localStorage.setItem(STORAGE_KEY, cur);
      localStorage.setItem(STORAGE_TS_KEY, Date.now());
    } catch (_) {
      cur = 100;
    }
    updateSocialText(el, cur);
  }

  // ============================================================
  // Shopify theme editor — re-init on section reload
  // ============================================================
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (e) {
      var sec = e.target.querySelector('[data-lkw-sneak-peek]');
      if (sec) setupSection(sec);
    });
  }

})();
